import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, MoreVertical, Upload, ListPlus, AlertCircle, ThumbsUp, ThumbsDown, Megaphone } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { companiesApi } from '@/services/api'
import { SelectCampaignDialog } from '@/components/select-campaign-dialog'
import { BulkPipelineToolbar, BulkAddToCampaignButton } from '@/components/bulk-pipeline-toolbar'
import { ImportCompaniesDialog } from '@/components/import-companies-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  PipelineStageBadge,
  PipelineStage,
  PIPELINE_STAGES,
  STAGE_CONFIG,
  StageProgressDots,
} from '@/components/pipeline-stage-badge'
import { ProspectRelationshipBadge } from '@/components/prospect-relationship-badge'
import { StageCounts } from '@/components/pipeline-progress'
import { Alert, AlertDescription } from '@/components/ui/alert'

const BUSINESS_TYPE_PLACEHOLDERS = new Set(['available services', 'services'])

function capitalizeWords(value: string): string {
  if (!value?.trim()) return value ?? ''
  return value
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

function isPlaceholderBusinessType(value: string): boolean {
  const lower = value.toLowerCase().trim()
  if (!lower) return true
  if (BUSINESS_TYPE_PLACEHOLDERS.has(lower)) return true
  if (lower.startsWith('available services')) return true
  if (lower === 'other') return true
  return false
}

interface Company {
  id: string
  name: string
  website: string
  industry: string
  company_type?: string | null
  company_type_tags?: string[]
  industry_tags?: string[]
  business_type?: string | null
  status: string
  source: string
  stage: PipelineStage
  prospect_relationship?: string | null
  created_at: string
  is_excluded?: boolean
}

/**
 * Resolves what to show in the Industry column.
 * Treats "available services" and similar placeholders as "unknown" and shows
 * "Analyzing..." instead of the raw placeholder text.
 */
function resolveIndustryDisplay(company: Company): { text: string; showAnalyzing: boolean } {
  const industry = (company.industry || '').trim()
  const companyType = (company.company_type || '').trim()
  const businessType = (company.business_type || '').trim()
  const businessTypeIsPlaceholder = isPlaceholderBusinessType(businessType)
  const companyTypeIsPlaceholder = isPlaceholderBusinessType(companyType)
  const typeTags = (company.company_type_tags || [])
    .map((t) => String(t).trim())
    .filter((t) => t && !isPlaceholderBusinessType(t))
  const industryTags = (company.industry_tags || [])
    .map((t) => String(t).trim())
    .filter((t) => t && !isPlaceholderBusinessType(t))

  const effective =
    industry ||
    (!companyTypeIsPlaceholder ? companyType : null) ||
    (!businessTypeIsPlaceholder ? businessType : null) ||
    typeTags[0] ||
    industryTags[0] ||
    ''

  const isEffectivePlaceholder = isPlaceholderBusinessType(effective)
  return {
    text: isEffectivePlaceholder ? '' : effective,
    showAnalyzing: isEffectivePlaceholder,
  }
}

export default function CompaniesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [activeStage, setActiveStage] = useState<PipelineStage | 'all'>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false)
  const [selectedCompanyForCampaign, setSelectedCompanyForCampaign] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [importDialogOpen, setImportDialogOpen] = useState(false)

  const excludeMutation = useMutation({
    mutationFn: (id: string) => companiesApi.exclude(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const includeMutation = useMutation({
    mutationFn: (id: string) => companiesApi.include(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const { data: companySources = [] } = useQuery<{ source_name: string; count: number }[]>({
    queryKey: ['companies', 'sources'],
    queryFn: () => companiesApi.getSources(),
    staleTime: 60000,
  })

  const {
    data: stageCountsData,
    isError: stageCountsError,
    error: stageCountsErrorDetail,
  } = useQuery<StageCounts>({
    queryKey: ['companies', 'stage-counts'],
    queryFn: () => companiesApi.getStageCounts(),
    staleTime: 30000,
  })

  const stageCounts = stageCountsData

  const {
    data,
    isLoading,
    isError: listError,
    error: listErrorDetail,
  } = useQuery<{ companies: Company[]; total: number }>({
    queryKey: ['companies', page, activeStage, search, sourceFilter],
    queryFn: () => companiesApi.list({
      page,
      per_page: 20,
      stage: activeStage === 'all' ? undefined : activeStage,
      search: search || undefined,
      source: sourceFilter === 'all' ? undefined : sourceFilter,
    }),
  })

  const companies = data?.companies || []
  const total = data?.total || 0

  const allSelected =
    companies.length > 0 && companies.every((c) => selectedIds.has(c.id))

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(companies.map((c) => c.id)))
    }
  }

  const toggleCompany = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedIds(next)
  }

  useEffect(() => {
    setSelectedIds(new Set())
  }, [page, activeStage, search, sourceFilter])

  const totalCompanies = stageCounts
    ? Object.values(stageCounts).reduce((a, b) => a + b, 0)
    : 0

  const handleStageChange = (stage: string) => {
    setActiveStage(stage as PipelineStage | 'all')
    setPage(1)
  }

  const handleSourceChange = (value: string) => {
    setSourceFilter(value)
    setPage(1)
  }

  const hasApiError = listError || stageCountsError
  const errorMessage =
    (listErrorDetail as Error)?.message ||
    (stageCountsErrorDetail as Error)?.message ||
    'Failed to load companies'

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Companies</h1>
          <p className="mt-1 text-muted-foreground">
            {total.toLocaleString()} companies in your pipeline
          </p>
        </div>
        <Button onClick={() => setImportDialogOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Import Companies
        </Button>
      </div>

      <ImportCompaniesDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />

      {hasApiError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {errorMessage}
            {errorMessage.includes('401') || errorMessage.includes('403')
              ? ' — Check that you are logged in and have organization access.'
              : ''}
          </AlertDescription>
        </Alert>
      )}

      {/* Stage tabs */}
      <Tabs value={activeStage} onValueChange={handleStageChange}>
        <TabsList className="h-auto flex-wrap gap-1 bg-transparent p-0">
          <TabsTrigger
            value="all"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4"
          >
            All
            <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1.5">
              {totalCompanies}
            </Badge>
          </TabsTrigger>
          {PIPELINE_STAGES.map((stage) => {
            const config = STAGE_CONFIG[stage]
            const Icon = config.icon
            const count = stageCounts?.[stage] ?? 0
            return (
              <TabsTrigger
                key={stage}
                value={stage}
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4"
              >
                <Icon className="mr-1.5 h-3.5 w-3.5" />
                {config.label}
                <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1.5">
                  {count}
                </Badge>
              </TabsTrigger>
            )
          })}
        </TabsList>
      </Tabs>

      {/* Search and filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search companies..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
                className="pl-10"
              />
            </div>
            <Select value={sourceFilter} onValueChange={handleSourceChange}>
              <SelectTrigger className="w-[200px] h-10">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All sources
                  {companySources.length > 0 &&
                    ` (${companySources.reduce((s, x) => s + x.count, 0)})`}
                </SelectItem>
                {companySources.map((src) => {
                  const label =
                    src.source_name === '__unknown__' ? '(Unknown)' : src.source_name
                  return (
                    <SelectItem key={src.source_name} value={src.source_name}>
                      {label} ({src.count})
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Companies table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead className="w-[60px]"></TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Data Source</TableHead>
              <TableHead>Relationship</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Added</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={10}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-16">
                  <div className="flex flex-col items-center gap-4 text-muted-foreground">
                    <p className="text-base font-medium">
                      {search || activeStage !== 'all'
                        ? 'No companies match your filters'
                        : 'No companies in your pipeline yet'}
                    </p>
                    {!search && activeStage === 'all' && (
                      <div className="flex flex-col sm:flex-row gap-3 items-center">
                        <Button asChild variant="default">
                          <Link to="/app/lists">
                            <ListPlus className="mr-2 h-4 w-4" />
                            Run a list to add companies
                          </Link>
                        </Button>
                        <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
                          <Upload className="mr-2 h-4 w-4" />
                          Import Companies
                        </Button>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company) => {
                const { text, showAnalyzing } = resolveIndustryDisplay(company)
                const isExcluded = company.is_excluded ?? false
                return (
                <TableRow
                  key={company.id}
                  className={cn(
                    "cursor-pointer",
                    isExcluded && "opacity-50"
                  )}
                  onClick={() => navigate(`/companies/${company.id}`)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(company.id)}
                      onCheckedChange={() => toggleCompany(company.id)}
                      aria-label={`Select ${company.name}`}
                    />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (isExcluded) {
                            includeMutation.mutate(company.id)
                          }
                        }}
                        disabled={!isExcluded || includeMutation.isPending}
                        className={cn(
                          "p-1 rounded hover:bg-muted transition-colors",
                          isExcluded ? "text-muted-foreground hover:text-green-600" : "text-green-600"
                        )}
                        title={isExcluded ? "Include company" : "Company included"}
                      >
                        <ThumbsUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!isExcluded) {
                            excludeMutation.mutate(company.id)
                          }
                        }}
                        disabled={isExcluded || excludeMutation.isPending}
                        className={cn(
                          "p-1 rounded hover:bg-muted transition-colors",
                          isExcluded ? "text-red-600" : "text-muted-foreground hover:text-red-600"
                        )}
                        title={isExcluded ? "Company excluded" : "Exclude company"}
                      >
                        <ThumbsDown className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-foreground">{company.name}</p>
                      {company.website && (
                        <a
                          href={company.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {company.website}
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {showAnalyzing ? (
                      <span className="text-sm italic">Analyzing...</span>
                    ) : text ? (
                      capitalizeWords(text)
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {company.source ? capitalizeWords(company.source) : '-'}
                  </TableCell>
                  <TableCell>
                    {company.prospect_relationship ? (
                      <ProspectRelationshipBadge
                        relationship={company.prospect_relationship}
                        size="sm"
                      />
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <PipelineStageBadge stage={company.stage} size="sm" />
                  </TableCell>
                  <TableCell>
                    <StageProgressDots stage={company.stage} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(company.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/companies/${company.id}`)}>
                          View details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedCompanyForCampaign(company.id)
                            setCampaignDialogOpen(true)
                          }}
                        >
                          <Megaphone className="h-4 w-4 mr-2" />
                          Add to Campaign
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            if (isExcluded) {
                              includeMutation.mutate(company.id)
                            } else {
                              excludeMutation.mutate(company.id)
                            }
                          }}
                        >
                          {isExcluded ? (
                            <>
                              <ThumbsUp className="h-4 w-4 mr-2" />
                              Include
                            </>
                          ) : (
                            <>
                              <ThumbsDown className="h-4 w-4 mr-2" />
                              Exclude
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive">Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )
            })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {total > 20 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page * 20 >= total}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <SelectCampaignDialog
        open={campaignDialogOpen}
        onOpenChange={(open) => {
          setCampaignDialogOpen(open)
          if (!open) {
            setSelectedCompanyForCampaign(null)
          }
        }}
        companyIds={
          selectedCompanyForCampaign
            ? [selectedCompanyForCampaign]
            : Array.from(selectedIds)
        }
        onSuccess={() => {
          setSelectedIds(new Set())
          setSelectedCompanyForCampaign(null)
        }}
      />

      <BulkPipelineToolbar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds(new Set())}
        invalidateQueries={[
          ['companies'],
          ['companies', 'stage-counts'],
        ]}
      >
        <BulkAddToCampaignButton
          selectedIds={selectedIds}
          onSelect={() => setCampaignDialogOpen(true)}
        />
      </BulkPipelineToolbar>
    </div>
  )
}
